import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { optClasses, OptToken } from '../optionColor';
import { ListStateShell } from '../ListStateShell';
import { useListFetch } from '../useListFetch';
import { useWriteFeedback } from '../useWriteFeedback';
import { ConfirmDialog } from '../ConfirmDialog';
import { BulkRentEditForm } from './BulkRentEditForm';

/**
 * UnitsGridCard — Part 5 of the real-estate module (realestate-dashboard-prd.md §5). Replaces the
 * mock `UNITS` const (PortfolioTab.tsx, pre-Part-5) with a real read-model over Unit + computed
 * occupancy (GET /api/properties/units-grid → PropertyService.getUnitsGrid, §0.3's occupancyOf(),
 * never a stored field).
 *
 * ONE CARD PER PROPERTY (§5.2) — deliberately not a single cross-property grid: a cell has no room
 * for a per-unit "Property" label the way RentRollCard's table does, so merging properties would make
 * a multi-property account's grid unreadable (AC5.3). Each cell is its own ≥44px tap target (AC5.4)
 * that opens the §0.4 drill-in overlay for that unit (AC5.2) — PortfolioTab owns the open/close state
 * and renders <UnitDrillInOverlay/> as a sibling, the same "parent holds the id, overlay is a sibling"
 * shape as agent-console's ticketCreatePrefill.
 *
 * I5/§3/§4/§6 — this header is also the Property-edit/delete/bulk-rent anchor (the realestate-I5-
 * manage-integrity-prd.md's own ruling: "the one place a Property is already rendered as its own
 * addressable block today"). Edit reuses the inline click-to-edit pattern (AccountDetailPanel's
 * startEdit/saveEdit); delete mirrors the now-fixed Company pre-offer-refusal + honest-409 pattern
 * (same PRD §2/§4) rather than a new one.
 */

type Occupancy = 'occupied' | 'on-notice' | 'vacant';
interface GridUnit { id: string; label: string; status: Occupancy }
interface GridProperty { id: string; name: string; address: string | null; units: GridUnit[] }

const STATUS_TOKEN: Record<Occupancy, OptToken> = { occupied: 'emerald', 'on-notice': 'amber', vacant: 'rose' };

export const UnitsGridCard: React.FC<{ onOpenUnit: (unitId: string) => void; onChanged?: () => void }> = ({ onOpenUnit, onChanged }) => {
  const { t } = useTranslation('common');
  const { status, data, retry } = useListFetch<GridProperty[]>('/api/properties/units-grid');
  const properties = data ?? [];
  const feedback = useWriteFeedback();

  // I5/§3/AC5.2 — one editingField slot for the whole card (never two properties mid-edit at once,
  // same single-slot shape AccountDetailPanel's editingField uses).
  const [editingProperty, setEditingProperty] = useState<{ id: string; field: 'name' | 'address' } | null>(null);
  const [propertyFieldDraft, setPropertyFieldDraft] = useState('');
  const startPropertyEdit = (id: string, field: 'name' | 'address', current: string | null) => {
    setPropertyFieldDraft(current ?? '');
    setEditingProperty({ id, field });
  };
  const savePropertyEdit = () => {
    if (!editingProperty) return;
    const { id, field } = editingProperty;
    setEditingProperty(null);
    const draft = propertyFieldDraft.trim();
    const prop = properties.find((p) => p.id === id);
    const orig = field === 'name' ? prop?.name : prop?.address;
    if (draft === (orig ?? '')) return; // no-op, nothing to save
    if (field === 'name' && !draft) return; // never persist a blanked-out name
    fetch(`/api/properties/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: field === 'name' ? draft : (draft || null) }),
    }).then((res) => {
      // On failure the field RE-OPENS with the draft intact (D-C5's rule) — never a silent revert.
      if (!res.ok) { feedback.fail(t('writeStates.saveFailed')); setEditingProperty({ id, field }); return; }
      onChanged?.();
    }).catch(() => { feedback.fail(t('writeStates.saveFailed')); setEditingProperty({ id, field }); });
  };

  // I5/§4/AC5.3 — delete, same two-layer shape Gap A just fixed on AccountDetailPanel: a pre-offer
  // check (unitCount is already loaded, no round-trip needed) picks blocked vs destructive BEFORE
  // the confirm renders; a 409 on attempt (a Unit added in the gap between render and click) is the
  // backstop, surfaced as a fail toast rather than a silent no-op.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const deleteProperty = async (id: string) => {
    setConfirmingDeleteId(null);
    const res = await fetch(`/api/properties/${id}`, { method: 'DELETE' }).catch(() => null);
    if (res?.status === 409) { feedback.fail(t('writeStates.actionFailed')); retry(); return; }
    if (!res?.ok) { feedback.fail(t('portfolio.unitsGrid.deleteFailed')); return; }
    onChanged?.();
  };

  // I5/§6 — the bulk-rent form, lazy-opened per property (its own read, not folded into the grid).
  const [bulkRentPropertyId, setBulkRentPropertyId] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-4" data-testid="portfolio-units-grid">
      <ListStateShell status={status} testIdBase="portfolio-units-grid" onRetry={retry}>
        {properties.map((p) => (
          <div key={p.id} className="bg-surface border border-border-neutral rounded-xl overflow-hidden" data-testid="portfolio-units-grid-property">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border-neutral">
              <div className="min-w-0 flex-1">
                {editingProperty?.id === p.id && editingProperty.field === 'name' ? (
                  <input autoFocus data-testid="portfolio-property-name-input" value={propertyFieldDraft}
                    onChange={(e) => setPropertyFieldDraft(e.target.value)} onBlur={savePropertyEdit}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingProperty(null); }}
                    className="w-full h-7 px-2 -mx-2 rounded-lg border border-primary text-sm font-bold" dir="auto" />
                ) : (
                  <h3 data-testid="portfolio-property-name" className="text-sm font-bold text-text-strong m-0 truncate cursor-text"
                    onClick={() => startPropertyEdit(p.id, 'name', p.name)}>
                    {t('portfolio.unitsGrid.title', { property: p.name })}
                  </h3>
                )}
                {editingProperty?.id === p.id && editingProperty.field === 'address' ? (
                  <input autoFocus data-testid="portfolio-property-address-input" value={propertyFieldDraft}
                    onChange={(e) => setPropertyFieldDraft(e.target.value)} onBlur={savePropertyEdit}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingProperty(null); }}
                    className="w-full h-6 px-2 -mx-2 mt-0.5 rounded-lg border border-primary text-xs" dir="auto" />
                ) : (
                  <div data-testid="portfolio-property-address" className="text-xs text-text-muted truncate cursor-text mt-0.5"
                    onClick={() => startPropertyEdit(p.id, 'address', p.address)}>
                    {p.address || <span className="italic text-text-muted/70">{t('portfolio.unitsGrid.addAddress')}</span>}
                  </div>
                )}
              </div>
              <button type="button" data-testid="portfolio-bulk-rent-open" onClick={() => setBulkRentPropertyId(p.id)}
                className="min-h-[44px] px-2 text-xs font-semibold text-primary hover:bg-primary/5 rounded-lg">
                {t('portfolio.setup.bulkRent.button')}
              </button>
              <AddUnit propertyId={p.id} onChanged={onChanged} />
              <button type="button" data-testid="portfolio-property-delete" onClick={() => setConfirmingDeleteId(p.id)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted hover:text-opt-rose"
                aria-label={t('portfolio.unitsGrid.delete')}>
                &times;
              </button>
            </div>
            {p.units.length === 0 ? (
              <div className="px-4 py-6 text-sm text-text-muted" data-testid="portfolio-units-grid-empty">{t('portfolio.unitsGrid.empty')}</div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 p-4">
                {p.units.map((u) => {
                  const { bg, fg } = optClasses(STATUS_TOKEN[u.status]);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      data-testid="portfolio-unit-cell"
                      aria-label={`${u.label} — ${t(`portfolio.unitsGrid.status.${u.status}`)}`}
                      // AC5.4 — min-h-[44px] EXPLICIT on the tap target itself, not left to the
                      // content to clear by accident (the mock's p-2 two-line cell measured short).
                      className={`min-h-[44px] rounded-md p-2 text-center ${bg} hover:opacity-90`}
                      onClick={() => onOpenUnit(u.id)}
                    >
                      <div className={`${fg} text-sm font-semibold truncate`}>{u.label}</div>
                      <div className={`${fg} text-[10px] mt-0.5`}>{t(`portfolio.unitsGrid.status.${u.status}`)}</div>
                    </button>
                  );
                })}
              </div>
            )}

            {confirmingDeleteId === p.id && (
              p.units.length > 0 ? (
                <ConfirmDialog blocked
                  message={t('portfolio.unitsGrid.deleteBlockedByUnits', { count: p.units.length })}
                  onClose={() => setConfirmingDeleteId(null)} />
              ) : (
                <ConfirmDialog destructive
                  message={t('portfolio.unitsGrid.deleteConfirm', { name: p.name })}
                  onConfirm={() => void deleteProperty(p.id)}
                  onClose={() => setConfirmingDeleteId(null)} />
              )
            )}

            {bulkRentPropertyId === p.id && (
              <BulkRentEditForm
                propertyId={p.id}
                propertyName={p.name}
                onClose={() => setBulkRentPropertyId(null)}
                onSaved={() => { setBulkRentPropertyId(null); onChanged?.(); }}
              />
            )}
          </div>
        ))}
      </ListStateShell>
      {feedback.node}
    </div>
  );
};

/**
 * AddUnit — the per-property "build up one unit at a time" entry (brief §2). A header-anchored button
 * that expands an inline label-only form (POST /api/properties/:id/units → { label }). On success it
 * calls onChanged, which refetches the dashboard so the new (vacant) cell appears in this grid — the
 * same onCreated-refetch contract PortfolioEmptyState uses. Lease assignment happens from the unit's
 * drill-in (an empty unit opens vacant → "Add lease"), not here, so this stays a one-field action.
 */
const AddUnit: React.FC<{ propertyId: string; onChanged?: () => void }> = ({ propertyId, onChanged }) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/properties/${propertyId}/units`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label.trim() }),
      });
      if (!res.ok) { setError(t('portfolio.setup.addUnit.error')); return; }
      setLabel('');
      setOpen(false);
      onChanged?.();
    } catch {
      setError(t('portfolio.setup.addUnit.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button type="button" data-testid="portfolio-add-unit" onClick={() => setOpen(true)}
        className="ms-auto min-h-[44px] px-3 rounded-lg border border-primary/40 text-xs font-semibold text-primary hover:bg-primary/5">
        + {t('portfolio.setup.addUnit.button')}
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="ms-auto flex items-center gap-2" data-testid="portfolio-add-unit-form">
      <input autoFocus required value={label} onChange={(e) => setLabel(e.target.value)} data-testid="portfolio-add-unit-label"
        placeholder={t('portfolio.setup.addUnit.placeholder')} dir="auto"
        className="h-10 px-2 rounded-lg border border-border-neutral text-sm w-24" />
      <button type="submit" disabled={!label.trim() || submitting} data-testid="portfolio-add-unit-submit"
        className="min-h-[44px] px-3 rounded-lg bg-primary text-white text-xs font-semibold disabled:opacity-50">
        {t('portfolio.setup.addUnit.submit')}
      </button>
      <button type="button" onClick={() => { setOpen(false); setLabel(''); setError(null); }} data-testid="portfolio-add-unit-cancel"
        className="min-h-[44px] px-2 text-xs font-semibold text-text-muted">
        {t('portfolio.setup.addUnit.cancel')}
      </button>
      {error && <span className="text-xs text-opt-rose" data-testid="portfolio-add-unit-error">{error}</span>}
    </form>
  );
};
